import React from "react";
import { Button, Input, Textarea } from "@heroui/react";
import { useTranslation } from "react-i18next";

import { FeatureCardProps } from "@/components/Chat/FeatureCards";

import { IconPicker } from "./IconPicker";

interface ValidationErrors {
  key?: string;
  title?: string;
  descriptions?: string;
}

interface ShortcutEditFormProps {
  shortcut: FeatureCardProps;
  validationErrors: ValidationErrors;
  isFormValid: boolean;
  onShortcutChange: (shortcut: FeatureCardProps) => void;
  onValidationErrorClear: (field: keyof ValidationErrors) => void;
  onSave: () => void;
  onCancel: () => void;
}

export const ShortcutEditForm: React.FC<ShortcutEditFormProps> = ({
  shortcut,
  validationErrors,
  isFormValid,
  onShortcutChange,
  onValidationErrorClear,
  onSave,
  onCancel,
}) => {
  const { t } = useTranslation();

  const handleKeyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onShortcutChange({ ...shortcut, key: e.target.value });
    if (validationErrors.key) {
      onValidationErrorClear("key");
    }
  };

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onShortcutChange({ ...shortcut, title: e.target.value });
    if (validationErrors.title) {
      onValidationErrorClear("title");
    }
  };

  const handleIconChange = (icon: string) => {
    onShortcutChange({ ...shortcut, icon });
  };

  const handleDescriptionsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onShortcutChange({
      ...shortcut,
      descriptions: e.target.value.split("\n"),
    });
    if (validationErrors.descriptions) {
      onValidationErrorClear("descriptions");
    }
  };

  return (
    <div className="space-y-2">
      <Input
        isRequired
        aria-label={t("Shortcut key input")}
        errorMessage={validationErrors.key}
        isInvalid={!!validationErrors.key}
        placeholder={t("Shortcut Key")}
        value={shortcut.key}
        onChange={handleKeyChange}
      />
      <Input
        isRequired
        aria-label={t("Shortcut title input")}
        errorMessage={validationErrors.title}
        isInvalid={!!validationErrors.title}
        placeholder={t("Shortcut Title")}
        value={shortcut.title}
        onChange={handleTitleChange}
      />
      <IconPicker
        aria-label={t("Shortcut icon input")}
        placeholder={t("Select an icon (optional)")}
        value={shortcut.icon}
        onChange={handleIconChange}
      />
      <Textarea
        isRequired
        aria-label={t("Shortcut descriptions textarea")}
        errorMessage={validationErrors.descriptions}
        isInvalid={!!validationErrors.descriptions}
        placeholder={t("Shortcut Descriptions (one per line)")}
        value={shortcut.descriptions.join("\n")}
        onChange={handleDescriptionsChange}
      />
      <div className="flex justify-end gap-2">
        <Button aria-label={t("Cancel editing")} variant="light" onClick={onCancel}>
          {t("Cancel")}
        </Button>
        <Button
          aria-label={t("Save shortcut")}
          className="btn-brand-primary"
          isDisabled={!isFormValid}
          onClick={onSave}
        >
          {t("Save")}
        </Button>
      </div>
    </div>
  );
};
